/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { ManageRoles } from "~/components/Role/ManageRoles";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import FlexContainer from "~/components/Styled/FlexContainer";
import { showSuccess } from "~/components/Toast";
import { Employee, RouteParams } from "~/models";
import { generateActionSuccessText, personToDropdown, useAxios } from "~/tools";
import { Role, TeamEmployee } from "./types";

type AssignMemberSidebarProps = {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (i: boolean) => void;
  refetch: () => void;
  refetchRoles: () => void;
  employees: Array<Employee>;
  roles: Array<Role>;
  team: TeamEmployee[];
};

function AssignMemberSidebar({
  isSidebarOpen,
  setIsSidebarOpen,
  employees,
  refetch,
  refetchRoles,
  roles,
  team,
}: AssignMemberSidebarProps) {
  const { customerId } = useParams<RouteParams>();

  const [isLoading, setIsLoading] = React.useState(false);

  const methods = useForm();
  const { showError } = useShowError();
  const { Axios } = useAxios();

  type Values = {
    employee: string;
    role: string;
  };

  const onSubmit = async (values: Values) => {
    const organizeValues = {
      employeeId: values.employee,
      roleIds: [values.role],
    };
    try {
      setIsLoading(true);
      await Axios().post(`/customers/${customerId}/employees`, organizeValues);
      setIsSidebarOpen(false);
      refetch();
      showSuccess(generateActionSuccessText(`Employee`)()(`added`)());
    } catch (e) {
      showError(e.response);
    }
    setIsLoading(false);
  };

  const findTeamEmployee = team?.map((t) => t?.id) || [];

  const filteredEmployees =
    employees && employees.filter(({ id }) => !findTeamEmployee.includes(id));

  const roleOpts =
    roles?.map(({ name, id }: Role) => ({
      label: name,
      value: id,
    })) || [];

  return (
    <SidebarForm
      title="Assign Employee"
      open={isSidebarOpen}
      setOpen={setIsSidebarOpen}
      formMethods={methods}
      submit={onSubmit}
    >
      <SidebarDropdown
        name="employee"
        label="Employee"
        options={filteredEmployees?.map(personToDropdown) || []}
        required
      />

      <SidebarDropdown name="role" label="Role" options={roleOpts} required />

      <FlexContainer>
        <Button
          type="submit"
          initialState={isLoading ? "loading" : ""}
          disabled={!methods.watch("employee") || !methods.watch("role")}
          css={{ marginRight: "auto" }}
        >
          Assign
        </Button>

        <ManageRoles fetchTeam={refetch} refetchRoles={refetchRoles} />
      </FlexContainer>
    </SidebarForm>
  );
}

export { AssignMemberSidebar };
